import React, { useState, useMemo, useEffect } from 'react';
import { pois } from './data/pois';
import type { City, LocalType, POI } from './types';
import { ITALY_VIEW, CITIES } from './constants';
import { MapComponent } from './components/MapComponent';

type MapView = {
  coordinates: { lat: number; lng: number };
  zoom: number;
};

const ALL_TYPES: LocalType[] = [
  "ristorante",
  "pizzeria",
  "gelateria",
  "pasticceria",
  "caffe",
  "bar",
  "osteria",
  "sushi",
  "street-food",
];

const App: React.FC = () => {
  const [activeFilters, setActiveFilters] = useState<LocalType[]>(ALL_TYPES);
  const [mapView, setMapView] = useState<MapView>(ITALY_VIEW);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedPoiId, setSelectedPoiId] = useState<string | null>(null);

  useEffect(() => {
    // Apri direttamente un POI se l'URL ha ?poi=<id>
    const urlParams = new URLSearchParams(window.location.search);
    const poiId = urlParams.get('poi');
    if (poiId) {
      const sharedPoi = pois.find(p => p.id === poiId);
      if (sharedPoi) {
        handleSelect(sharedPoi);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (type: LocalType) => {
    setActiveFilters(prev => {
      const next = prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type];
      // Se l'utente deseleziona tutto, riattiviamo tutti i filtri
      // (preserva il comportamento precedente).
      if (next.length === 0) return ALL_TYPES;
      return next;
    });
  };

  const handleSelect = (item: City | POI) => {
    if ('zoom' in item) {
      // E' una citta'
      setMapView({ coordinates: item.coordinates, zoom: item.zoom || 12 });
    } else {
      // E' un POI
      setMapView({ coordinates: item.coordinates, zoom: 16 });
      setSelectedPoiId(item.id);
    }
    setIsPanelOpen(false);
  };

  const handleSearch = async (query: string) => {
    if (!query.trim()) return;
    const lowerCaseQuery = query.toLowerCase();

    // 1. Match esatto su una citta' nota
    const knownCity = CITIES.find(city => city.name.toLowerCase() === lowerCaseQuery);
    if (knownCity) {
      handleSelect(knownCity);
      return;
    }

    // 2. Match su un POI (prima "starts with", poi "includes")
    let poiMatch = pois.find(p => p.name.toLowerCase().startsWith(lowerCaseQuery));
    if (!poiMatch) {
      poiMatch = pois.find(p => p.name.toLowerCase().includes(lowerCaseQuery));
    }
    if (poiMatch) {
      handleSelect(poiMatch);
      return;
    }

    // 3. Fallback geocoding tramite Nominatim
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}, Italy&format=json&limit=1&accept-language=it`
      );
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();

      if (data && data.length > 0) {
        const { lat, lon, display_name } = data[0];
        const newCity: City = {
          name: display_name,
          coordinates: { lat: parseFloat(lat), lng: parseFloat(lon) },
          zoom: 12,
        };
        handleSelect(newCity);
      } else {
        alert(`Spiacente, non ho trovato "${query}". Prova con un'altra citta' o locale in Italia.`);
      }
    } catch (error) {
      console.error("Error during geocoding search:", error);
      alert("Problema con il servizio di ricerca. Riprova piu' tardi.");
    }
  };

  const handleLocateUser = async () => {
    // Prova prima il plugin Capacitor Geolocation (nativo iOS/Android).
    // Su iOS la web API navigator.geolocation non sempre triggera il prompt
    // anche con NSLocationWhenInUseUsageDescription. Il plugin nativo
    // gestisce il prompt + permessi in modo affidabile su entrambe le
    // piattaforme. Se il plugin non e' presente (web o dev), fallback alla
    // web API standard.
    const onSuccess = (lat: number, lng: number) => {
      const userCoords = { lat, lng };
      setUserLocation(userCoords);
      setMapView({ coordinates: userCoords, zoom: 15 });
    };
    const onError = (msg: string) => {
      console.error("Error getting user location:", msg);
      alert("Non riesco a recuperare la tua posizione. Verifica che la geolocalizzazione sia abilitata nelle impostazioni del dispositivo.");
    };

    try {
      const { Capacitor } = await import("@capacitor/core");
      if (Capacitor.isNativePlatform()) {
        try {
          const { Geolocation } = await import("@capacitor/geolocation");
          // Richiedi i permessi (mostra prompt se necessario)
          const perm = await Geolocation.requestPermissions();
          if (perm.location !== "granted") {
            onError("permesso negato");
            return;
          }
          const position = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 10000,
          });
          onSuccess(position.coords.latitude, position.coords.longitude);
          return;
        } catch (e) {
          console.warn("[Geo] Capacitor plugin failed, falling back to web API:", e);
          // continua sotto col fallback web
        }
      }
    } catch {
      // Capacitor non disponibile (web build), continua col fallback
    }

    // Fallback web (navigator.geolocation)
    if (!navigator.geolocation) {
      onError("Geolocation non supportata dal browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => onSuccess(position.coords.latitude, position.coords.longitude),
      (error) => onError(error.message),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const filteredPois = useMemo(() => {
    if (activeFilters.length === ALL_TYPES.length) return pois;
    return pois.filter(poi => activeFilters.includes(poi.type));
  }, [activeFilters]);

  return (
    <main className="relative h-screen w-screen bg-gray-100 font-sans">
      <MapComponent
        pois={filteredPois}
        allPois={pois}
        mapView={mapView}
        userLocation={userLocation}
        selectedPoiId={selectedPoiId}
        isPanelOpen={isPanelOpen}
        onTogglePanel={() => setIsPanelOpen(!isPanelOpen)}
        onLocateUser={handleLocateUser}
        onSearch={handleSearch}
        onSelect={handleSelect}
        activeFilters={activeFilters}
        onFilterChange={handleFilterChange}
      />
    </main>
  );
};

export default App;
